'use client'

import React, { FC, ReactElement } from 'react'
import styles from './WalletStats.module.css'
import classNames from 'classnames'
import CollectionStat from '@/components/collection-stat'
import Wallet from '@/utils/wallet'
import Link from '@/components/link'
import useWalletBalance from '@/utils/hooks/useWalletBalance'
import InscriptionBalanceModal from '@/components/inscription-balance-modal'
import { WalletTabs } from '../wallet-item-feed'
import groupBy from '@/utils/group-by'
import CollectionSatoshiStat from '@/components/collection-satoshi-stat'
import useProfile from '@/utils/hooks/useProfile'
import useSatoshiPriceUSD from '@/utils/hooks/useSatoshiPriceUSD'
import useUSDToggle from '@/utils/hooks/useUSDToggle'

const WalletStats: FC<{
	className?: string
	itemCount: number
	isLoadingItems: boolean
	soldCount: number
	isLoadingSold: boolean
	setSelectedTab: (tab: WalletTabs) => void
}> = (props): ReactElement => {
	const { itemCount, isLoadingItems, soldCount, isLoadingSold, setSelectedTab } = props

	const [showTooltip, setShowTooltip] = React.useState(false)
	const [showUSD, _setShowUSD] = useUSDToggle()

	const { data: wallet, isLoading: isLoadingWallet } = useWalletBalance()
	const { data: profile, isLoading: isLoadingProfile } = useProfile(Wallet.getTaprootAddress())

	const collections = groupBy(
		wallet?.inscriptions?.filter((i) => i?.collection?.slug),
		(i) => i?.collection?.slug || ''
	)

	let collectionSatoshis = 0
	for (const [_key, value] of Object.entries(collections)) {
		const collection = value[0]?.collection
		// @ts-ignore
		const satoshis = collection?.floor_price * value?.length
		collectionSatoshis += satoshis
	}

	const brc20Satoshis = wallet?.brc20
		?.map((c) => {
			let satoshiPrice = 0
			if (c?.floor_price_per && c?.overall_balance) {
				satoshiPrice = c.floor_price_per * c.overall_balance
			}

			return satoshiPrice
		})
		?.reduce((acc, e) => acc + e, 0)

	const totalSatoshis =
		brc20Satoshis + collectionSatoshis + wallet?.unconfirmed_balance + wallet?.confirmed_balance

	const [priceUSD, _priceUSDisLoading] = useSatoshiPriceUSD(wallet?.inscription_balance)

	const _inscriptionBalance = React.useMemo(() => {
		let inscriptionBalanceText = ''
		if (isLoadingWallet || !wallet?.inscription_balance) {
			inscriptionBalanceText += '0.00000000 BTC'
		} else {
			inscriptionBalanceText += (wallet?.inscription_balance / 1e8).toFixed(8)
			inscriptionBalanceText += ' BTC'
		}
		if (showUSD) {
			inscriptionBalanceText = `$${priceUSD}`
		}
		return inscriptionBalanceText
	}, [isLoadingWallet, wallet?.inscription_balance, showUSD, priceUSD])

	const listedCount = wallet?.inscriptions?.filter((i) => i?.escrow)?.length

	const stats = [
		{
			value: wallet?.inscription_balance,
			isLoading: isLoadingWallet,
			title: 'Inscription balance',
			//icon: BitcoinIcon,
			//onClick: () => setShowTooltip(true)
			satoshiStat: true
		},
		{
			value: totalSatoshis,
			isLoading: isLoadingWallet,
			title: 'Total value',
			satoshiStat: true
		},
		{
			value: itemCount,
			isLoading: isLoadingItems,
			title: 'Total items',
			onClick: () => setSelectedTab('show-all')
		},
		{
			value: soldCount,
			isLoading: isLoadingSold,
			title: 'Sold items',
			onClick: () => setSelectedTab('Sold')
		},
		{
			value: listedCount,
			isLoading: isLoadingWallet,
			title: 'Listed items',
			onClick: () => setSelectedTab('Listed')
		},
		{
			value: 'View',
			isLoading: isLoadingProfile,
			title: 'Public collection',
			href: profile?.sns ? `/${profile?.sns}` : `/address/${Wallet.getTaprootAddress()}`
		}
	]

	const renderStatCard = (stat: any, index: any) => {
		if (stat?.href) {
			return (
				<Link href={stat.href} key={index}>
					<CollectionStat title={stat.title} value={stat.value} />
				</Link>
			)
		}

		if (stat?.satoshiStat) {
			return (
				<CollectionSatoshiStat
					key={index}
					title={stat.title}
					satoshis={stat.value}
					isLoading={stat.isLoading}
				/>
			)
		}

		return (
			<CollectionStat
				key={index}
				onClick={stat?.onClick}
				isLoading={stat?.isLoading}
				title={stat.title}
				value={stat.value}
			/>
		)
	}

	return (
		<div className={classNames(styles.root, { [(props?.className as any)]: !!props.className })}>
			{stats.map(renderStatCard)}
			{showTooltip && (
				<InscriptionBalanceModal open={showTooltip} onClose={() => setShowTooltip(false)} />
			)}
		</div>
	)
}

export default WalletStats
